/**
 * @file TopNav.jsx
 * @description Top navigation bar. Renders role-appropriate nav items, the
 *   notification bell with badge, unread inbox indicator, mobile bottom tab bar,
 *   and the user account dropdown with sign out.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module components/TopNav
 */
import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { T, roleColor, roleTypeKey, typeLabel } from "../theme";
import { Btn } from "./UI";
import Logo from "./Logo";
import { NotificationBell, JobAlertsModal } from "./Notifications";
import ContactSupportModal from "./ContactSupportModal";
import ContactModal from "./ContactModal";
import { UserIcon, BellIcon, BuildingIcon, DollarIcon, LogOutIcon, ChevronDownIcon, MapPinIcon, BriefcaseIcon, LockIcon, MessageIcon, ClockIcon, UsersIcon } from "./Icons";
import { useIsMobile } from "../hooks/useIsMobile";
import { getUnreadConversationCount, subscribeToInboxMessages } from "../lib/messageService";
import { loadPendingInviteCount } from "../lib/applicationService";
import { isTherapistRole, therapistRoleIds } from "../lib/roles";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { track, A, ctaMeta } from "../lib/analytics";
import { isAgencyPageView } from "../lib/agencyPages";
import { getAgencyPricing } from "../lib/agencyPricing";
import { setSignupIntent, clearSignupIntent } from "../lib/signupIntent";
import { useConfigVersion } from "../lib/useConfigVersion";
import { useStickyCtaVisible } from "../lib/stickyCtaSignal";
import ProPill from "./ui/ProPill";
import { OWNER_ONLY_VIEWS } from "../lib/agencyContext";

// Profile dropdown menu item ids per role (labels resolved via t() at render time)
// Team logins: a teammate (not the owner) never sees these (lib/agencyContext canManageAgency).
const OWNER_ONLY_MENU_IDS = OWNER_ONLY_VIEWS;

const PROFILE_MENU_IDS = {
  rbt:    [
    { id: "profile",       icon: <UserIcon size={15} />,     labelKey: "nav.myProfile"            },
    { id: "notifications", icon: <BellIcon size={15} />,     labelKey: "nav.notificationSettings" },
    { id: "tracker",       icon: <ClockIcon size={15} />,    labelKey: "tracker.nav"              },
  ],
  bcba:   [
    { id: "profile",       icon: <UserIcon size={15} />,     labelKey: "nav.myProfile"            },
    { id: "notifications", icon: <BellIcon size={15} />,     labelKey: "nav.notificationSettings" },
    { id: "tracker",       icon: <ClockIcon size={15} />,    labelKey: "tracker.nav"              },
  ],
  bcaba:  [
    { id: "profile",       icon: <UserIcon size={15} />,     labelKey: "nav.myProfile"            },
    { id: "notifications", icon: <BellIcon size={15} />,     labelKey: "nav.notificationSettings" },
    { id: "tracker",       icon: <ClockIcon size={15} />,    labelKey: "tracker.nav"              },
  ],
  agency: [
    { id: "agencyabout",   icon: <BuildingIcon size={15} />, labelKey: "nav.agencyProfile"        },
    // Team logins are Pro: Starter agencies see a PRO tag (the screen previews it with an upgrade).
    { id: "team",          icon: <UsersIcon size={15} />,    labelKey: "nav.team", proTag: true   },
    { id: "notifications", icon: <BellIcon size={15} />,     labelKey: "nav.notificationSettings" },
    { id: "billing",       icon: <DollarIcon size={15} />,   labelKey: "nav.billing"              },
  ],
  admin:  [],
};

// Provider roles are written by theme.js typeLabel; only the non-provider accounts have a label of their own here.
const ROLE_LABEL = {
  agency: "Agency",
  admin:  "Admin",
};

// Credential codes (RBT, BCBA, BCaBA) are the same in every language; the
// agency and admin labels are words, so they are translated.
const ROLE_LABEL_KEY = { agency: "nav.roleAgency", admin: "nav.roleAdmin" };
export function roleLabel(t, role) {
  if (ROLE_LABEL_KEY[role]) return t(ROLE_LABEL_KEY[role]);
  const key = roleTypeKey(role);
  return key ? typeLabel(key) : ROLE_LABEL[role];
}

// Provider roles take their color from theme.js (roleColor, the same as the case-type chips everywhere);
// only the non-provider accounts (agency, admin) have a color of their own here.
const ACCOUNT_COLOR = {
  agency: { color: T.blue,    bg: T.blueLight   },
  admin:  { color: "#7c3aed", bg: "#ede9fe"     },
};
function roleAccent(userRole) {
  if (ACCOUNT_COLOR[userRole]) return ACCOUNT_COLOR[userRole];
  const c = roleColor(roleTypeKey(userRole) ? userRole : "rbt"); // guests and unknown roles read as RBT, as before
  return { color: c.accent, bg: c.bg };
}

// ── Bottom tab bar tabs per role ─────────────────────────────────────────────
// Star is always yellow — Best Matches is the premium differentiator
const StarTabIcon = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <path
      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
      fill="#FFD700"
      stroke={active ? "#E6B800" : "#FFD700"}
      strokeWidth="1.5"
    />
  </svg>
);

function getBottomTabs(userRole, isProfileComplete, t) {
  const therapistTabs = [
    { id: "cases-v2",     label: t('nav.findCases'),   icon: (c) => <MapPinIcon size={22} color={c} />         },
    { id: "matches",      label: t('nav.bestMatches'), icon: (_c, active) => <StarTabIcon active={active} />, locked: !isProfileComplete && userRole !== "guest" },
    { id: "applications", label: t('nav.applications'), icon: (c) => <BriefcaseIcon size={22} color={c} />      },
  ];
  const map = {
    // Guests get no app bottom-bar — they navigate the public site via the
    // simple marketing top nav, never logged-in app tabs.
    guest:  [],
    rbt:    therapistTabs,
    bcba:   therapistTabs,
    bcaba:  therapistTabs,
    agency: [],
    admin: [],
  };
  return map[userRole] || [];
}

function getInitials(name) {
  if (!name?.trim()) return null;
  const parts = name.trim().split(/\s+/);
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getFirstName(name) {
  if (!name?.trim()) return null;
  return name.trim().split(/\s+/)[0];
}

function ProfileDropdown({ userRole, userName, userPhotoUrl, view, setView, onClose, onSignOut, onContactSupport, teamMember = false, agencyPro = false }) {
  const { t } = useTranslation();
  const items    = (PROFILE_MENU_IDS[userRole] || [])
    .filter(item => !(teamMember && OWNER_ONLY_MENU_IDS.has(item.id)))
    .map(item => ({ ...item, label: t(item.labelKey) }));
  const rc       = roleAccent(userRole);
  const initials = getInitials(userName);

  return (
    <div style={{
      position: "absolute",
      top: "calc(100% + 8px)",
      right: 0,
      background: T.surface,
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
      minWidth: 220,
      zIndex: 9999,
      overflow: "hidden",
      animation: "dropIn 0.14s ease",
    }}>
      <style>{`@keyframes dropIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }`}</style>

      {/* Header */}
      <div style={{
        padding: "14px 16px",
        borderBottom: `1px solid ${T.border}`,
        background: T.surfaceAlt,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 38, height: 38, borderRadius: "50%",
            background: rc.bg,
            border: `1.5px solid ${rc.color}44`,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, overflow: "hidden",
          }}>
            {userPhotoUrl ? (
              <img
                src={userPhotoUrl}
                alt={userName || "Profile"}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : initials ? (
              <span style={{
                fontSize: 13, fontWeight: 700, color: rc.color,
                fontFamily: "'JetBrains Mono', monospace", lineHeight: 1,
              }}>
                {initials}
              </span>
            ) : (
              <UserIcon size={20} color={rc.color} />
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: 13, fontWeight: 700, color: T.text,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {userName || t('nav.myAccount')}
            </div>
            <div style={{ marginTop: 3 }}>
              <span style={{
                fontSize: 10, fontWeight: 700,
                color: rc.color, background: rc.bg,
                borderRadius: 5, padding: "1px 7px",
                fontFamily: "'JetBrains Mono', monospace",
                letterSpacing: 0.3,
              }}>
                {roleLabel(t, userRole)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Menu items */}
      <div style={{ padding: "6px 0" }}>
        {items.map(item => {
          const isActive = view === item.id;
          return (
            <button
              key={item.id}
              onClick={() => { setView(item.id); onClose(); }}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                width: "100%", padding: "9px 16px",
                background: isActive ? T.blueLight : "transparent",
                border: "none", cursor: "pointer",
                fontFamily: "'Inter', 'DM Sans', sans-serif",
                textAlign: "left",
                transition: "background 0.12s",
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = T.surfaceAlt; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{ display: "flex", alignItems: "center", flexShrink: 0, color: isActive ? T.blue : T.textMid }}>{item.icon}</span>
              <span style={{ fontSize: 13, fontWeight: isActive ? 700 : 500, color: isActive ? T.blue : T.textMid }}>
                {item.label}
              </span>
              {item.proTag && !agencyPro && (
                <ProPill data-testid="menu-pro-tag" />
              )}
              {isActive && (
                <span style={{ marginLeft: "auto", width: 6, height: 6, borderRadius: "50%", background: T.blue, flexShrink: 0 }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Language — moved out of the top bar; toggles locale in place */}
      <div style={{ borderTop: `1px solid ${T.border}`, padding: "6px 0" }}>
        <LanguageSwitcher menuRow onAfterSwitch={onClose} />
      </div>

      {/* Contact support */}
      <div style={{ borderTop: `1px solid ${T.border}`, padding: "6px 0" }}>
        <button
          onClick={() => { onContactSupport(); onClose(); }}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            width: "100%", padding: "9px 16px",
            background: "transparent", border: "none", cursor: "pointer",
            fontFamily: "'Inter', 'DM Sans', sans-serif", textAlign: "left",
            transition: "background 0.12s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = T.surfaceAlt; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
        >
          <span style={{ display: "flex", alignItems: "center" }}><MessageIcon size={16} color={T.textMid} /></span>
          <span style={{ fontSize: 13, fontWeight: 500, color: T.textMid }}>{t('nav.contactSupport')}</span>
        </button>
      </div>

      {/* Sign out */}
      <div style={{ borderTop: `1px solid ${T.border}`, padding: "6px 0" }}>
        <button
          onClick={() => { onSignOut(); onClose(); }}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            width: "100%", padding: "9px 16px",
            background: "transparent", border: "none", cursor: "pointer",
            fontFamily: "'Inter', 'DM Sans', sans-serif", textAlign: "left",
            transition: "background 0.12s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = T.surfaceAlt; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
        >
          <span style={{ display: "flex", alignItems: "center", color: T.textMid }}><LogOutIcon size={15} /></span>
          <span style={{ fontSize: 13, fontWeight: 500, color: T.textMid }}>{t('nav.signOut')}</span>
        </button>
      </div>
    </div>
  );
}

/**
 * Shown on every screen of a test account (profiles.is_test): its openings and profile are hidden from real users and
 * it only pairs with other test accounts (server rule: is_test_account / _test_visible, migration 20261104120000).
 */
function TestAccountPill({ style }) {
  const { t } = useTranslation();
  return (
    <span title={t("nav.testAccountHint")} data-testid="test-account-pill" style={{
      display: "inline-flex", alignItems: "center", flexShrink: 0, padding: "2px 8px", borderRadius: 99,
      fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", color: T.yellow,
      background: T.yellowLight, border: `1px solid ${T.yellow}40`, ...style,
    }}>{t("nav.testAccount")}</span>
  );
}

export default function TopNav({ view, setView, userRole, userName, userPhotoUrl, userEmail, showJobAlerts, setShowJobAlerts, alertInitialFilters, onJobAlertsClose, isProfileComplete, onSignOut, userId, agencyId, teamMember = false, agencyPro = false, onOpenInboxThread, onOpenApplicationThread, onOpenApplicantCard, onHighlightMatchCase, onAuth, isTestAccount = false }) {
  const { t, i18n } = useTranslation();
  // Open the auth screen in a specific mode ("signup" | "login"). Falls back to
  // plain navigation if the parent didn't supply a mode-aware handler.
  const goAuth = (mode) => (onAuth ? onAuth(mode) : setView("auth"));
  const stickyCtaOn = useStickyCtaVisible(); // phones: the sticky bar replaces the header CTA while it is up
  useConfigVersion(); // the agency CTA label depends on the Admin-configured promo
  // Guest header CTA by context: on agency pages it is the agency's own action
  // ("Post a case free", promo-aware; "Post a case" after the promo) and starts an agency
  // sign-up (Agency preselected, same intent the agency pages' CTAs set). Everywhere else
  // it stays the provider-default "Sign up free".
  const agencyCta = userRole === "guest" && isAgencyPageView(view);
  // Small phones (under 360 wide): the short label, so the Menu button never gets pushed off screen
  // ("Publicar un caso gratis" did at 320; mobile audit 2026-10-21).
  const narrowPhone = useIsMobile(360);
  const guestCtaLabel = agencyCta
    ? t(getAgencyPricing(i18n.language).isPromoActive && !narrowPhone ? "nav.postCaseFree" : "nav.postCase")
    : t(narrowPhone ? "nav.signUp" : "nav.signUpFree");
  const guestSignup = () => {
    track(A.NAV_LINK_CLICK, {
      supabaseMeta: { view: "auth" },
      ga4Params: ctaMeta({
        pagePath: window.location.pathname,
        pageType: "nav",
        ctaLabel: agencyCta ? "Post a case free" : "Sign up free",
        targetPath: "/auth",
        userLanguage: i18n.language,
        ...(agencyCta ? { audience: "agency" } : {}),
      }),
    });
    // Agency pages set the agency intent; the generic header CTA clears any stored intent so an
    // earlier page-specific CTA ("Start tracking free") cannot leak into this sign-up.
    if (agencyCta) setSignupIntent("agency");
    else clearSignupIntent();
    goAuth("signup");
  };
  // Sliding underline indicator for the desktop nav: measure the active item's
  // position so a single bar can animate between tabs.
  const navRowRef = useRef(null);
  const navItemRefs = useRef({});
  const [navIndicator, setNavIndicator] = useState(null); // { left, width } | null
  useLayoutEffect(() => {
    const measure = () => {
      const el = navItemRefs.current[view];
      setNavIndicator(el ? { left: el.offsetLeft, width: el.offsetWidth } : null);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [view, userRole, i18n.language]);
  const [dropdownOpen,  setDropdownOpen]  = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSupport,   setShowSupport]   = useState(false);
  // Guest-only "Reach out" contact form — same public ContactModal used by the
  // footer, surfaced in the nav itself so it isn't buried at the bottom of the
  // page (guests previously had zero nav-level contact entry point).
  const [showGuestContact, setShowGuestContact] = useState(false);
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  // Pending agency invitations for therapists — drives the numeric badge on
  // the "Best Matches" nav item so direct invites surface immediately, not
  // only after the therapist navigates into Best Matches.
  const [pendingInviteCount, setPendingInviteCount] = useState(0);
  const dropdownRef = useRef(null);
  const isMobile = useIsMobile();

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [dropdownOpen]);

  // Close dropdowns on view change
  useEffect(() => { setDropdownOpen(false); setMobileMenuOpen(false); }, [view]);

  // Center app tabs are for signed-in therapists only — guests get the simple
  // marketing nav (Find ABA Cases / Free Payment Tracker / For Agencies) instead,
  // never logged-in app links like Best Matches or My Applications.
  const navItems = [
    { id: "cases-v2",        label: t('nav.findCases'),        role: [...therapistRoleIds()] },
    { id: "matches",         label: t('nav.bestMatches'),      role: [...therapistRoleIds()], locked: !isProfileComplete && userRole !== "guest" },
    { id: "applications",    label: t('nav.applications'),     role: [...therapistRoleIds()] },
  ].filter(n => n.role.includes(userRole));

  // "For Agencies" only shown to guests
  const showForAgencies = userRole === "guest";
  const showForTherapists = userRole === "guest";

  const showInbox = isTherapistRole(userRole) || userRole === "agency";
  // The inbox badge and the bell are the agency's: a teammate reads them under the agency id.
  const scopeId = agencyId || userId;
  const showBell  = showInbox;

  useEffect(() => {
    if (!showInbox || !scopeId) {
      setInboxUnreadCount(0);
      return;
    }

    let alive = true;
    const refreshUnread = () => {
      getUnreadConversationCount(scopeId, userRole)
        .then(count => { if (alive) setInboxUnreadCount(count); })
        .catch(() => { if (alive) setInboxUnreadCount(0); });
    };

    refreshUnread();
    const unsub = subscribeToInboxMessages(scopeId, refreshUnread);
    return () => {
      alive = false;
      unsub?.();
    };
  }, [showInbox, scopeId, userRole]);

  useEffect(() => {
    if (view !== "inbox" || !showInbox || !scopeId) return;
    getUnreadConversationCount(scopeId, userRole)
      .then(setInboxUnreadCount)
      .catch(() => {});
  }, [view, showInbox, scopeId, userRole]);

  // Pending agency invites — therapist roles only. Re-checks when the user
  // returns to Best Matches (so accept/decline actions there decrement the
  // badge without a page refresh) and on every userId change.
  const isTherapist = isTherapistRole(userRole);
  useEffect(() => {
    if (!isTherapist || !userId) {
      setPendingInviteCount(0);
      return;
    }
    let alive = true;
    loadPendingInviteCount(userId)
      .then(c => { if (alive) setPendingInviteCount(c); })
      .catch(() => { if (alive) setPendingInviteCount(0); });
    return () => { alive = false; };
  }, [isTherapist, userId, view]); // include `view` so navigating to "matches" refreshes

  const profileViews = (PROFILE_MENU_IDS[userRole] || []).map(i => i.id);
  const isProfileView = profileViews.includes(view);

  const bottomTabs = getBottomTabs(userRole, isProfileComplete, t);
  const isLoggedIn = userRole !== "guest";

  // ── Bottom tab bar (mobile only — all users including guests) ───────────────
  const bottomTabBar = isMobile && bottomTabs.length > 0 && createPortal(
    <nav style={{
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 1900,
      background: "rgba(255,255,255,0.96)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      borderTop: `1px solid ${T.border}`,
      display: "flex",
      alignItems: "stretch",
      paddingBottom: "env(safe-area-inset-bottom, 0px)",
      boxShadow: "0 -2px 12px rgba(15,23,42,0.07)",
    }}>
      {bottomTabs.map(tab => {
        const active = view === tab.id;
        const color  = active ? T.blue : T.textLight;
        return (
          <button
            key={tab.id}
            onClick={() => {
              if (tab.locked) {
                setView("profile"); // nudge to complete profile
              } else {
                setView(tab.id);
              }
            }}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              paddingTop: 10,
              paddingBottom: 10,
              minHeight: 60,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              position: "relative",
              transition: "opacity 0.12s",
              opacity: tab.locked ? 0.45 : 1,
            }}
          >
            {/* Icon — wrapped in pill background when active */}
            <span style={{
              display: "flex", alignItems: "center", position: "relative",
              padding: active ? "5px 18px" : "5px 4px",
              borderRadius: 999,
              background: active ? T.blueLight : "transparent",
              transition: "all 0.15s",
            }}>
              {tab.icon(color, active)}
              {tab.locked && (
                <span style={{
                  position: "absolute",
                  top: -4,
                  right: -6,
                  lineHeight: 1,
                }}><LockIcon size={9} color="currentColor" /></span>
              )}
              {tab.id === "matches" && !tab.locked && pendingInviteCount > 0 && (
                <span style={{
                  position: "absolute", top: -6, right: -10,
                  background: T.orange, color: "#fff",
                  borderRadius: 999, padding: "1px 5px",
                  fontSize: 9, fontWeight: 800, lineHeight: 1.2,
                  fontFamily: "'JetBrains Mono', monospace",
                  border: "1.5px solid #fff", minWidth: 14, textAlign: "center",
                }}>{pendingInviteCount}</span>
              )}
            </span>
            {/* Label */}
            <span style={{
              fontSize: 10,
              fontWeight: active ? 700 : 500,
              color,
              letterSpacing: "0.01em",
              fontFamily: "'Inter', 'DM Sans', sans-serif",
            }}>
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>,
    document.body
  );

  return (
    <header style={{
      background: T.surface,
      borderBottom: `1px solid ${T.border}`,
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: isMobile ? "0 16px" : "0 24px",
      height: 56,
      flexShrink: 0,
      boxShadow: "0 1px 0 rgba(15,23,42,0.05), 0 4px 16px rgba(15,23,42,0.04)",
      zIndex: 2000,
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
    }}>

      {/* ── MOBILE layout ── */}
      {isMobile ? (
        <>
          {/* Logo — clickable, navigates to role-appropriate home */}
          <img
            src="/ABAcases-mark.svg" alt="ABAcases"
            onClick={() => setView(userRole === "agency" ? "dashboard" : userRole === "admin" ? "admin" : "cases-v2")}
            style={{ height: 40, objectFit: "contain", cursor: "pointer" }}
          />
          {isTestAccount && <TestAccountPill />}

          {/* Right side */}
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            {/* Language switcher — guests only in the bar: the globe + dropdown
                menu, same as desktop. Logged-in users switch language from the
                profile dropdown (LanguageSwitcher menuRow), so nothing shows
                here for them. */}
            {userRole === "guest" && (
              <>
                <LanguageSwitcher globeMenu />
                {/* Thin divider between lang switcher and the guest CTAs */}
                <div style={{ width: 1, height: 20, background: T.border, flexShrink: 0 }} />
              </>
            )}
            {/* Guest: keep the bar uncrowded — Sign up free stays the visible
                primary CTA; the marketing links + Log in live in a compact menu. */}
            {userRole === "guest" && (
              <>
                {!stickyCtaOn && (
                  <Btn
                    variant="cta"
                    onClick={guestSignup}
                    style={{ padding: "0 14px", minHeight: 44, fontSize: 12, borderRadius: T.radiusMd }}
                  >
                    {guestCtaLabel}
                  </Btn>
                )}
                <div style={{ position: "relative" }}>
                  <button
                    aria-label={t('nav.menu')}
                    onClick={() => setMobileMenuOpen(o => !o)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, flexShrink: 0, background: mobileMenuOpen ? T.blueLight : "none", border: `1px solid ${mobileMenuOpen ? T.blue + "55" : T.border}`, borderRadius: 8, cursor: "pointer" }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.text} strokeWidth="2" strokeLinecap="round">
                      <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
                    </svg>
                  </button>
                  {mobileMenuOpen && (
                    <>
                      <div onClick={() => setMobileMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 2400 }} />
                      <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: 210, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: "0 8px 32px rgba(15,23,42,0.16)", overflow: "hidden", zIndex: 2500, padding: "6px 0" }}>
                        {[
                          { view: "aba-case-finder", label: t('nav.findAbaCases'), color: T.blue },
                          { view: "free-aba-timesheet-payment-tracker", label: t('nav.freePaymentTracker'), color: T.blue },
                          { view: "aba-agencies", label: t('nav.forAgencies'), color: T.blue },
                          { view: "aba-resources", label: t('nav.abaResources'), color: T.blue },
                        ].map((item) => (
                          <button
                            key={item.view}
                            onClick={() => {
                              track(A.NAV_LINK_CLICK, {
                                supabaseMeta: { view: item.view },
                                ga4Params: ctaMeta({
                                  pagePath: window.location.pathname,
                                  pageType: "nav",
                                  ctaLabel: item.label,
                                  targetPath: `/${item.view}`,
                                  userLanguage: i18n.language,
                                }),
                              });
                              setMobileMenuOpen(false);
                              setView(item.view);
                            }}
                            style={{ display: "block", width: "100%", textAlign: "left", padding: "11px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, color: item.color, fontFamily: "'Inter', 'DM Sans', sans-serif" }}
                          >
                            {item.label}
                          </button>
                        ))}
                        <div style={{ height: 1, background: T.border, margin: "5px 0" }} />
                        <button
                          onClick={() => {
                            track(A.NAV_LINK_CLICK, {
                              supabaseMeta: { view: "contact-modal" },
                              ga4Params: ctaMeta({
                                pagePath: window.location.pathname,
                                pageType: "nav",
                                ctaLabel: "Contact",
                                targetPath: "contact-modal",
                                userLanguage: i18n.language,
                              }),
                            });
                            setMobileMenuOpen(false);
                            setShowGuestContact(true);
                          }}
                          style={{ display: "block", width: "100%", textAlign: "left", padding: "11px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, color: T.textMid, fontFamily: "'Inter', 'DM Sans', sans-serif" }}
                        >
                          {t('nav.contactSupport')}
                        </button>
                        <div style={{ height: 1, background: T.border, margin: "5px 0" }} />
                        <button
                          onClick={() => { setMobileMenuOpen(false); goAuth("login"); }}
                          style={{ display: "block", width: "100%", textAlign: "left", padding: "11px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, color: T.textMid, fontFamily: "'Inter', 'DM Sans', sans-serif" }}
                        >
                          {t('nav.logIn')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            {/* Logged-in: bell + inbox + avatar */}
            {isLoggedIn && showBell && (
              <NotificationBell
                userRole={userRole}
                setView={setView}
                onOpenJobAlerts={() => setShowJobAlerts(true)}
                userId={scopeId}
                onOpenInboxThread={onOpenInboxThread}
                onOpenApplicationThread={onOpenApplicationThread}
                onOpenApplicantCard={onOpenApplicantCard}
                onHighlightMatchCase={onHighlightMatchCase}
              />
            )}
            {isLoggedIn && showInbox && (
              <button
                onClick={() => setView("inbox")}
                style={{
                  minWidth: 44, minHeight: 44, borderRadius: "50%",
                  background: view === "inbox" ? T.blueLight : "transparent",
                  border: `1px solid ${view === "inbox" ? T.blue + "44" : "transparent"}`,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.15s",
                  position: "relative",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={view === "inbox" ? T.blue : T.textMid} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
                  <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/>
                </svg>
                {inboxUnreadCount > 0 && (
                  <span style={{
                    position: "absolute",
                    top: 3,
                    right: 2,
                    minWidth: 17,
                    height: 17,
                    padding: "0 4px",
                    borderRadius: 999,
                    background: T.blue,
                    color: "#fff",
                    fontSize: 9,
                    fontWeight: 800,
                    fontFamily: "'JetBrains Mono', monospace",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 0 0 2px #fff",
                    lineHeight: 1,
                  }}>
                    {inboxUnreadCount > 9 ? "9+" : inboxUnreadCount}
                  </span>
                )}
              </button>
            )}
            {isLoggedIn && (
              <div ref={dropdownRef} style={{ position: "relative" }}>
                <button
                  onClick={() => setDropdownOpen(o => !o)}
                  style={{
                    display: "flex", alignItems: "center",
                    padding: "4px",
                    borderRadius: "50%",
                    border: `1px solid ${isProfileView || dropdownOpen ? T.blue + "66" : T.border}`,
                    background: isProfileView || dropdownOpen ? T.blueLight : T.surface,
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {(() => {
                    const rc = roleAccent(userRole);
                    const active = isProfileView || dropdownOpen;
                    const initials = getInitials(userName);
                    return (
                      <div style={{
                        width: 28, height: 28, borderRadius: "50%",
                        background: active ? rc.color : rc.bg,
                        border: `1.5px solid ${active ? rc.color : rc.color + "44"}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, overflow: "hidden", transition: "all 0.15s",
                      }}>
                        {userPhotoUrl ? (
                          <img src={userPhotoUrl} alt={userName || "Profile"} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
                        ) : initials ? (
                          <span style={{ fontSize: 11, fontWeight: 700, color: active ? "#fff" : rc.color, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>{initials}</span>
                        ) : (
                          <UserIcon size={15} color={active ? "#fff" : rc.color} />
                        )}
                      </div>
                    );
                  })()}
                </button>
                {dropdownOpen && (
                  <ProfileDropdown
                    teamMember={teamMember}
                    agencyPro={agencyPro}
                    userRole={userRole}
                    userName={userName}
                    userPhotoUrl={userPhotoUrl}
                    view={view}
                    setView={setView}
                    onClose={() => setDropdownOpen(false)}
                    onSignOut={onSignOut}
                    onContactSupport={() => setShowSupport(true)}
                  />
                )}
              </div>
            )}
          </div>

          {/* Bottom tab bar portal */}
          {bottomTabBar}
        </>
      ) : (
        <>
          {/* ── DESKTOP layout ── */}
          {/* Logo */}
          <Logo
            variant="lockup"
            height={32}
            onClick={() => setView(userRole === "agency" ? "dashboard" : userRole === "admin" ? "admin" : "cases-v2")}
            style={{ marginRight: isTestAccount ? 8 : 16 }}
          />
          {isTestAccount && <TestAccountPill style={{ marginRight: 12 }} />}

          {/* Nav links — sliding underline indicator marks the active tab */}
          <div ref={navRowRef} style={{ position: "relative", display: "flex", alignItems: "center", gap: 2 }}>
            {navItems.flatMap((n, i) => {
              const btn = (
                <button
                  key={n.id}
                  ref={el => { navItemRefs.current[n.id] = el; }}
                  onClick={() => !n.locked && setView(n.id)}
                  title={n.locked ? t('nav.bestMatchesLocked') : undefined}
                  style={{
                    position: "relative",
                    padding: "6px 12px 8px",
                    borderRadius: 8,
                    fontSize: 13,
                    fontFamily: "'Inter', 'DM Sans', sans-serif",
                    cursor: n.locked ? "not-allowed" : "pointer",
                    border: "none",
                    // Faint selection tint on the active item, on top of the
                    // sliding underline (subtle — not the old solid pill).
                    background: view === n.id ? `${T.blue}14` : "transparent",
                    color:      n.locked ? T.textXLight : (view === n.id ? T.blue : T.textMid),
                    fontWeight: view === n.id ? 700 : 500,
                    opacity: n.locked ? 0.45 : 1,
                    display: "flex", alignItems: "center", gap: 5,
                    transition: "color 0.15s, background 0.15s",
                    WebkitTapHighlightColor: "transparent",
                  }}
                  onMouseEnter={e => { if (!n.locked && view !== n.id) e.currentTarget.style.background = `${T.blue}0a`; }}
                  onMouseLeave={e => { if (!n.locked && view !== n.id) e.currentTarget.style.background = "transparent"; }}
                >
                  {n.label}
                  {n.locked && <span style={{ opacity: 0.7 }}><LockIcon size={10} color="currentColor" /></span>}
                  {n.id === "matches" && !n.locked && pendingInviteCount > 0 && (
                    <span title={t('nav.invitationsWaiting', { count: pendingInviteCount })}
                      style={{
                        background: T.orange, color: "#fff", borderRadius: 10,
                        padding: "1px 7px", fontSize: 10, fontWeight: 800,
                        fontFamily: "'JetBrains Mono', monospace",
                      }}>
                      {pendingInviteCount}
                    </span>
                  )}
                  {n.id === "matches" && !n.locked && pendingInviteCount === 0 && (
                    // Gold star = premium differentiator (matches the mobile tab
                    // star). Intentionally NOT green — green is reserved for the CTA.
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }} aria-hidden="true">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                        fill="#FFD700" stroke="#E6B800" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              );
              if (i === 0) return [btn];
              // Faint vertical separator between items.
              return [
                <div key={`${n.id}-sep`} style={{
                  width: 1, height: 16, alignSelf: "center", flexShrink: 0,
                  background: T.border, opacity: 0.7, margin: "0 2px",
                }} />,
                btn,
              ];
            })}
            {/* Sliding underline — animates between the active items */}
            {navIndicator && (
              <div style={{
                position: "absolute", bottom: 0,
                left: navIndicator.left + 12,
                width: Math.max(0, navIndicator.width - 24),
                height: 2, borderRadius: 2,
                background: T.blue,
                transition: "left 0.28s cubic-bezier(0.4,0,0.2,1), width 0.28s cubic-bezier(0.4,0,0.2,1)",
                pointerEvents: "none",
              }} />
            )}
          </div>

          {/* Right side */}
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            {/* Therapist / Agency landing links — guests only, plain text style */}
            {(showForTherapists || showForAgencies) && (
              <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                {[
                  { view: "aba-case-finder", label: t('nav.findAbaCases'), accent: T.blue, active: view === "aba-case-finder" },
                  { view: "aba-agencies", label: t('nav.forAgencies'), accent: T.blue, active: view === "aba-agencies" },
                  { view: "aba-resources", label: t('nav.abaResources'), accent: T.blue, active: view === "aba-resources" },
                ].map((item) => (
                  <button
                    key={item.view}
                    onClick={() => {
                      track(A.NAV_LINK_CLICK, {
                        supabaseMeta: { view: item.view },
                        ga4Params: ctaMeta({
                          pagePath: window.location.pathname,
                          pageType: "nav",
                          ctaLabel: item.label,
                          targetPath: `/${item.view}`,
                          userLanguage: i18n.language,
                        }),
                      });
                      setView(item.view);
                    }}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 8,
                      fontSize: 13,
                      fontFamily: "'Inter', 'DM Sans', sans-serif",
                      cursor: "pointer",
                      border: "none",
                      whiteSpace: "nowrap",
                      background: item.active ? `${item.accent}14` : "transparent",
                      color: item.active ? item.accent : (item.accent === T.green ? T.green : T.textMid),
                      fontWeight: item.active ? 700 : (item.accent === T.green ? 600 : 500),
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { if (!item.active) { e.currentTarget.style.color = item.accent; e.currentTarget.style.background = `${item.accent}0a`; } }}
                    onMouseLeave={e => { if (!item.active) { e.currentTarget.style.color = item.accent === T.green ? T.green : T.textMid; e.currentTarget.style.background = "transparent"; } }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
            {/* Zone divider — separates audience wayfinding (For Therapists /
                Agencies) from the utility + account cluster (language + auth),
                so the right side reads as two distinct zones. */}
            {userRole === "guest" && (showForTherapists || showForAgencies) && (
              <div style={{ width: 1, height: 20, background: T.border, opacity: 0.7, marginLeft: 4, marginRight: 4, flexShrink: 0 }} />
            )}
            {/* Language switcher — guests only in the bar (globe menu). For
                logged-in users it lives in the profile dropdown instead, so the
                bar stays clean; guests have no menu, so they keep a one-tap
                toggle here, next to (never replacing) the auth CTAs. */}
            {userRole === "guest" && <LanguageSwitcher globeMenu />}
            {/* Contact — icon-only, same visual weight as the globe. Guests had
                no nav-level way to reach a human before this; low-frequency,
                high-trust-value action, so it stays a quiet icon rather than a
                peer to the audience-wayfinding text links. */}
            {userRole === "guest" && (
              <button
                onClick={() => {
                  track(A.NAV_LINK_CLICK, {
                    supabaseMeta: { view: "contact-modal" },
                    ga4Params: ctaMeta({
                      pagePath: window.location.pathname,
                      pageType: "nav",
                      ctaLabel: "Contact",
                      targetPath: "contact-modal",
                      userLanguage: i18n.language,
                    }),
                  });
                  setShowGuestContact(true);
                }}
                title={t('nav.contactSupport')}
                aria-label={t('nav.contactSupport')}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: 36, height: 36, borderRadius: 8,
                  background: "none", border: `1px solid ${T.border}`,
                  cursor: "pointer", flexShrink: 0,
                }}
              >
                <MessageIcon size={17} color={T.textMid} />
              </button>
            )}
            {userRole === "guest" ? (
              <>
                <Btn variant="outline" onClick={() => goAuth("login")} style={{ padding: "7px 16px", fontSize: 12, borderRadius: T.radiusMd }}>{t('nav.logIn')}</Btn>
                <Btn
                  variant="cta"
                  onClick={guestSignup}
                  style={{ padding: "7px 16px", fontSize: 12, borderRadius: T.radiusMd }}
                >
                  {guestCtaLabel}
                </Btn>
              </>
            ) : (
              <>
                {showBell && (
                  <NotificationBell
                    asPill
                    userRole={userRole}
                    setView={setView}
                    onOpenJobAlerts={() => setShowJobAlerts(true)}
                    userId={scopeId}
                    onOpenInboxThread={onOpenInboxThread}
                    onOpenApplicationThread={onOpenApplicationThread}
                    onOpenApplicantCard={onOpenApplicantCard}
                    onHighlightMatchCase={onHighlightMatchCase}
                  />
                )}

                {showInbox && (
                  <button
                    onClick={() => setView("inbox")}
                    style={{
                      position: "relative",
                      display: "flex", alignItems: "center", gap: 5,
                      padding: "5px 12px", borderRadius: T.radiusFull,
                      background: view === "inbox" ? T.blueLight : T.surfaceAlt,
                      border: `1px solid ${view === "inbox" ? T.blue + "44" : T.border}`,
                      cursor: "pointer", transition: "all 0.15s",
                      fontSize: 12, fontWeight: view === "inbox" ? 600 : 500,
                      color: view === "inbox" ? T.blue : T.textMid,
                      fontFamily: "'Inter', 'DM Sans', sans-serif",
                      paddingRight: inboxUnreadCount > 0 ? 34 : 12,
                    }}
                    onMouseEnter={e => { if (view !== "inbox") { e.currentTarget.style.background = T.surfaceAlt; e.currentTarget.style.borderColor = T.blue + "44"; } }}
                    onMouseLeave={e => { if (view !== "inbox") { e.currentTarget.style.background = T.surfaceAlt; e.currentTarget.style.borderColor = T.border; } }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
                      <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/>
                    </svg>
                    {t('nav.inbox')}
                    {inboxUnreadCount > 0 && (
                      <span style={{
                        position: "absolute",
                        top: "50%",
                        right: 8,
                        transform: "translateY(-50%)",
                        minWidth: 18,
                        height: 18,
                        padding: "0 5px",
                        borderRadius: 999,
                        background: T.blue,
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 800,
                        fontFamily: "'JetBrains Mono', monospace",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1,
                      }}>
                        {inboxUnreadCount > 9 ? "9+" : inboxUnreadCount}
                      </span>
                    )}
                  </button>
                )}

                {/* Profile avatar → dropdown for settings/sign out */}
                <div ref={dropdownRef} style={{ position: "relative" }}>
                  <button
                    onClick={() => setDropdownOpen(o => !o)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "5px 10px 5px 6px",
                      borderRadius: 24,
                      border: `1px solid ${isProfileView || dropdownOpen ? T.blue + "66" : T.border}`,
                      background: isProfileView || dropdownOpen ? T.blueLight : T.surface,
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                  >
                    {(() => {
                      const rc      = roleAccent(userRole);
                      const active  = isProfileView || dropdownOpen;
                      const initials = getInitials(userName);
                      return (
                        <div style={{
                          width: 28, height: 28, borderRadius: "50%",
                          background: active ? rc.color : rc.bg,
                          border: `1.5px solid ${active ? rc.color : rc.color + "44"}`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0, overflow: "hidden",
                          transition: "all 0.15s",
                        }}>
                          {userPhotoUrl ? (
                            <img
                              src={userPhotoUrl}
                              alt={userName || "Profile"}
                              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                            />
                          ) : initials ? (
                            <span style={{
                              fontSize: 11, fontWeight: 700,
                              color: active ? "#fff" : rc.color,
                              fontFamily: "'JetBrains Mono', monospace",
                              lineHeight: 1,
                            }}>
                              {initials}
                            </span>
                          ) : (
                            <UserIcon size={15} color={active ? "#fff" : rc.color} />
                          )}
                        </div>
                      );
                    })()}
                    <span style={{
                      fontSize: 13, fontWeight: 600,
                      color: isProfileView || dropdownOpen ? T.blue : T.textMid,
                      maxWidth: 120, overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {getFirstName(userName) || roleLabel(t, userRole)}
                    </span>
                    <ChevronDownIcon
                      size={12}
                      color={isProfileView || dropdownOpen ? T.blue : T.textLight}
                      style={{
                        transform: dropdownOpen ? "rotate(180deg)" : "none",
                        transition: "transform 0.15s",
                        flexShrink: 0,
                      }}
                    />
                  </button>

                  {dropdownOpen && (
                    <ProfileDropdown
                      teamMember={teamMember}
                    agencyPro={agencyPro}
                      userRole={userRole}
                      userName={userName}
                      userPhotoUrl={userPhotoUrl}
                      view={view}
                      setView={setView}
                      onClose={() => setDropdownOpen(false)}
                      onSignOut={onSignOut}
                      onContactSupport={() => setShowSupport(true)}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Job Alerts Modal */}
      <JobAlertsModal
        open={showJobAlerts}
        onClose={onJobAlertsClose || (() => setShowJobAlerts(false))}
        onGoToSettings={() => {
          setShowJobAlerts(false);
          if (onJobAlertsClose) onJobAlertsClose();
          setView("notifications");
        }}
        userId={userId}
        userRole={userRole}
        initialFilters={alertInitialFilters}
      />

      {/* Contact Support Modal */}
      {showSupport && (
        <ContactSupportModal
          profile={{ id: userId, full_name: userName, email: userEmail, role: userRole }}
          onClose={() => setShowSupport(false)}
        />
      )}

      {/* Guest "Reach out" contact form — nav-level entry point (desktop icon
          + mobile menu row above), same public form as the footer's Contact link. */}
      {showGuestContact && <ContactModal onClose={() => setShowGuestContact(false)} />}
    </header>
  );
}
